import { patch } from "@rails/request.js"
import { useState } from "react"

import { Modal } from "../shared/Modal"
import type { StayAssignment, StayAssignmentType, StayPlaybookTemplates } from "./stayData"

const assignmentLabels = {
  guide: "Guide",
  waiter: "Waiter",
} satisfies Record<StayAssignmentType, string>

const playbookKeyByAssignmentType = {
  guide: "guide_name",
  waiter: "waiter_name",
} satisfies Record<StayAssignmentType, keyof StayPlaybookTemplates>

type StayAssignmentsSectionProps = {
  assignments: readonly StayAssignment[]
  playbookTemplates: StayPlaybookTemplates
  stayId: string
}

export function StayAssignmentsSection({
  assignments,
  playbookTemplates,
  stayId,
}: StayAssignmentsSectionProps) {
  const [assignmentRows, setAssignmentRows] = useState([...assignments])
  const [editingAssignment, setEditingAssignment] = useState<StayAssignment | null>(null)
  const [selectedOptionId, setSelectedOptionId] = useState("")
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  function editAssignment(assignment: StayAssignment) {
    setError(null)
    setSelectedOptionId(assignment.option_id)
    setEditingAssignment(assignment)
  }

  async function saveAssignment(assignment: StayAssignment) {
    setSaving(true)
    setError(null)

    try {
      const response = await patch(`/api/stays/${stayId}/assignment`, {
        body: {
          stay_assignment: {
            assignment_type: assignment.assignment_type,
            option_id: selectedOptionId,
          },
        },
        responseKind: "json",
      })
      const updatedAssignment = await response.json as Pick<StayAssignment, "assignment_type" | "option_id" | "value">

      setAssignmentRows((currentRows) =>
        currentRows.map((currentRow) =>
          currentRow.assignment_type === updatedAssignment.assignment_type
            ? { ...currentRow, option_id: updatedAssignment.option_id, value: updatedAssignment.value }
            : currentRow,
        ),
      )
      document.querySelector<HTMLElement>(`[data-stay-assignment-value="${updatedAssignment.assignment_type}"]`)!.textContent =
        updatedAssignment.value || "-"
      setEditingAssignment(null)
    } catch {
      setError("Assignment could not be saved.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="stay-assignment-list">
      <dl>
        {assignmentRows.map((assignment) => (
          <div className="stay-assignment-row" key={assignment.assignment_type}>
            <div>
              <dt>{assignmentLabels[assignment.assignment_type]}</dt>
              <dd>{assignment.value || "-"}</dd>
            </div>
            <button
              className="stay-button stay-button--secondary"
              onClick={() => editAssignment(assignment)}
              type="button"
            >
              {assignment.value ? "Edit" : `Assign ${assignmentLabels[assignment.assignment_type]}`}
            </button>
          </div>
        ))}
      </dl>

      {editingAssignment ? (
        <Modal
          footer={
            <>
              <button
                className="stay-button stay-button--secondary"
                disabled={saving}
                onClick={() => setEditingAssignment(null)}
                type="button"
              >
                Cancel
              </button>
              <button
                className="stay-button stay-button--primary"
                disabled={saving}
                onClick={() => void saveAssignment(editingAssignment)}
                type="button"
              >
                {saving ? "Saving" : "Save"}
              </button>
            </>
          }
          onClose={() => setEditingAssignment(null)}
          open
          title={`${assignmentLabels[editingAssignment.assignment_type]} assignment`}
        >
          {error ? <p className="stay-error">{error}</p> : null}
          {playbookTemplates[playbookKeyByAssignmentType[editingAssignment.assignment_type]] ? (
            <aside className="stay-playbook-guidance">
              <h4>Playbook guidance</h4>
              <p>{playbookTemplates[playbookKeyByAssignmentType[editingAssignment.assignment_type]]}</p>
            </aside>
          ) : null}
          <div className="stay-form-field">
            <label htmlFor="stay_assignment_option">
              {assignmentLabels[editingAssignment.assignment_type]}
            </label>
            <select
              id="stay_assignment_option"
              onChange={(event) => setSelectedOptionId(event.target.value)}
              value={selectedOptionId}
            >
              <option value="">No {assignmentLabels[editingAssignment.assignment_type].toLowerCase()} assigned</option>
              {editingAssignment.options.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          </div>
        </Modal>
      ) : null}
    </div>
  )
}
